import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import { useForkRef, useEnhancedEffect, useEventCallback } from '../../hooks'
import {
  useFormControl,
  formControlState,
  FormControlContext,
  FormControlState,
} from '../FormControl'
import { useNumberInput, UseNumberInputProps } from '../InputNumber'
import { TextareaAutosize } from '../TextAreaAutosize'
import { isFilled, isHostComponent } from '../utils'
import style from './index.module.scss'
import { IconButton } from '../IconButton'
import InputChevronUp from '../internal/svg-icons/InputChevronUp'
import InputChevronDown from '../internal/svg-icons/InputChevronDown'
import { Box } from '../Box'
import { Typography } from '../Typography'
import { capitalize } from '../../utils'

interface NumberInputProps
  extends Omit<
    UseNumberInputProps,
    | 'readOnly'
    | 'disabled'
    | 'onFocus'
    | 'onBlur'
    | 'onChange'
    | 'defaultValue'
    | 'value'
  > {
  incrementButtonIcon?: JSX.Element
  decrementButtonIcon?: JSX.Element
  buttonsContainerClass?: string
}

export interface InputBaseClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if the component is a descendant of `FormControl`. */
  formControl: string
  /** Styles applied to the root element if the component is focused. */
  focused: string
  /** Styles applied to the root element if `disabled={true}`. */
  disabled: string
  /** Styles applied to the root element if `startAdornment` is provided. */
  adornedStart: string
  /** Styles applied to the root element if `endAdornment` is provided. */
  adornedEnd: string
  /** Styles applied to the wrapper of adornedCenterWrapper and Input */
  adornedCenterWrapper: string
  /** State class applied to the root element if `error={true}`. */
  error: string
  /** Styles applied to the input element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the input element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the input element if `size="large"`. */
  sizeLarge: string
  /** Styles applied to the root element if `multiline={true}`. */
  multiline: string
  /** Styles applied to the root element if the color is secondary. */
  colorSecondary: string
  /** Styles applied to the root element if `fullWidth={true}`. */
  fullWidth: string
  /** State class applied to the root element if `readOnly={true}`. */
  readOnly: string
  /** Styles applied to the input element. */
  input: string
  /** Styles applied to the input element if `size="small"`. */
  inputSizeSmall: string
  /** Styles applied to the input element if `size="medium"`. */
  inputSizeMedium: string
  /** Styles applied to the input element if `size="large"`. */
  inputSizeLarge: string
  /** Styles applied to the input element if `disabled={true}`. */
  inputDisabled: string
  /** Styles applied to the input element if `multiline={true}`. */
  inputMultiline: string
  /** Styles applied to the input element if `type="search"`. */
  inputTypeSearch: string
  /** Styles applied to the input element if `startAdornment` is provided. */
  inputAdornedStart: string
  /** Styles applied to the input element if `endAdornment` is provided. */
  inputAdornedEnd: string
}

interface InputBaseComponentProps
  extends Omit<
    React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    'onBlur' | 'onFocus' | 'onChange'
  > {
  // Accommodate arbitrary additional props coming from the `inputProps` prop
  [arbitrary: string]: any
}

export interface InputBaseProps
  extends Omit<
    Partial<React.HTMLAttributes<HTMLDivElement>>,
    /*
     * `onBlur`, `onChange`, `onFocus`, `onInvalid`, `onKeyDown`, `onKeyUp` are applied to the inner `InputComponent`,
     * which by default is an input or textarea. Since these handlers differ from the
     * ones inherited by `React.HTMLAttributes<HTMLDivElement>` we need to omit them.
     */
    | 'children'
    | 'defaultValue'
    | 'onBlur'
    | 'onChange'
    | 'onFocus'
    | 'onInvalid'
    | 'onKeyDown'
    | 'onKeyUp'
  > {
  'aria-describedby'?: string
  /**
   * This prop helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
   * @default "off"
   */
  autoComplete?: string
  /**
   * If `true`, the `input` element is focused during the first mount.
   */
  autoFocus?: boolean
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<InputBaseClasses>
  /**
   * The color of the component.
   * The prop defaults to the value (`'primary'`) inherited from the parent FormControl component.
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: unknown
  /**
   * If `true`, the component is disabled.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  disabled?: boolean
  /**
   * End `InputAdornment` for this component.
   */
  endAdornment?: React.ReactNode
  /**
   * Center `InputAdornment` for this component.
   */
  centerAdornment?: React.ReactNode
  /**
   * If `true`, the `input` will indicate an error.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  error?: boolean
  /**
   * If `true`, the `input` will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * The id of the `input` element.
   */
  id?: string
  /**
   * The component used for the `input` element.
   * Either a string to use a HTML element or a component.
   * @default 'input'
   */
  inputComponent?: React.ElementType<InputBaseComponentProps>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   * @default {}
   */
  inputProps?: InputBaseComponentProps
  /**
   * Pass a ref to the `input` element.
   */
  inputRef?: React.Ref<any>
  /**
   * If `true`, a [TextareaAutosize](/material-ui/react-textarea-autosize/) element is rendered.
   * @default false
   */
  multiline?: boolean
  /**
   * Name attribute of the `input` element.
   */
  name?: string
  /**
   * Callback fired when the `input` is blurred.
   *
   * Notice that the first argument (event) might be undefined.
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Callback fired when the value is changed.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onKeyUp?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>
  /**
   * Callback fired when the `input` doesn't satisfy its constraints.
   */
  onInvalid?: React.FormEventHandler<HTMLInputElement | HTMLTextAreaElement>
  /**
   * The short hint displayed in the `input` before the user enters a value.
   */
  placeholder?: string
  /**
   * It prevents the user from changing the value of the field
   * (not from interacting with the field).
   */
  readOnly?: boolean
  /**
   * If `true`, the `input` element is required.
   * The prop defaults to the value (`false`) inherited from the parent FormControl component.
   */
  required?: boolean
  renderSuffix?: (state: {
    disabled?: boolean
    error?: boolean
    filled?: boolean
    focused?: boolean
    required?: boolean
    startAdornment?: React.ReactNode
  }) => React.ReactNode
  /**
   * Number of rows to display when multiline option is set to true.
   */
  rows?: string | number
  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  maxRows?: string | number
  /**
   * Minimum number of rows to display when multiline option is set to true.
   */
  minRows?: string | number
  /**
   * The size of the component.
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Start `InputAdornment` for this component.
   */
  startAdornment?: React.ReactNode
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
   * @default 'text'
   */
  type?: string
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value?: unknown
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * This prop is an alias for the `componentsProps` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slotProps?: {
    root?: React.HTMLAttributes<HTMLDivElement>
    input?: React.InputHTMLAttributes<HTMLInputElement>
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    root?: React.ElementType
    input?: React.ElementType
  }
  /**
   * Will set Input component to use Number Input
   * @default false
   */
  numberInput?: boolean
  /**
   * Input Number Props passed to input component when `numberInput` is true
   */
  NumberInputProps?: NumberInputProps
}

export const InputBase = React.forwardRef<HTMLDivElement, InputBaseProps>(
  function InputBase(props, ref) {
    const {
      'aria-describedby': ariaDescribedby,
      autoComplete = 'off',
      autoFocus,
      className,
      classes,
      color,
      defaultValue,
      disabled,
      endAdornment,
      error,
      fullWidth = false,
      id,
      inputComponent = 'input',
      inputProps: inputPropsProp = {} as InputBaseComponentProps,
      inputRef: inputRefProp,
      maxRows,
      minRows,
      multiline = false,
      name,
      onBlur,
      onChange,
      onClick,
      onFocus,
      onKeyDown,
      onKeyUp,
      placeholder,
      readOnly,
      renderSuffix,
      rows,
      size,
      slots = {},
      slotProps = {},
      startAdornment,
      centerAdornment,
      type = 'text',
      value: valueProp,
      sx,
      numberInput = false,
      NumberInputProps,
      // @ts-ignore
      label: _,
      ...other
    } = props

    const value =
      inputPropsProp.value != null ? inputPropsProp.value : valueProp
    const { current: isControlled } = React.useRef(value != null)

    const inputRef = React.useRef<HTMLInputElement>()
    const handleInputRefWarning = React.useCallback((instance: HTMLElement) => {
      if (process.env.NODE_ENV !== 'production') {
        if (instance && instance.nodeName !== 'INPUT' && !instance.focus) {
          console.error(
            [
              'You have provided a `inputComponent` to the input component',
              'that does not correctly handle the `ref` prop.',
              'Make sure the `ref` prop is called with a HTMLInputElement.',
            ].join('\n')
          )
        }
      }
    }, [])

    const handleInputRef = useForkRef(
      inputRef,
      inputRefProp,
      inputPropsProp.ref,
      handleInputRefWarning
    )

    const [focused, setFocused] = React.useState(false)
    const factorFormControl = useFormControl()

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      React.useEffect(() => {
        if (factorFormControl) {
          return factorFormControl.registerEffect()
        }

        return undefined
      }, [factorFormControl])
    }

    const fcs = formControlState({
      props: { color, disabled, error, size, ...other },
      factorFormControl,
      states: ['color', 'disabled', 'error', 'size', 'required', 'filled'],
    })

    fcs.focused = factorFormControl ? factorFormControl.focused : focused

    // The blur won't fire when the disabled state is set on a focused input.
    // We need to book keep the focused state manually.
    React.useEffect(() => {
      if (!factorFormControl && disabled && focused) {
        setFocused(false)
        if (onBlur) {
          //@ts-ignore
          onBlur()
        }
      }
    }, [factorFormControl, disabled, focused, onBlur])

    const onFilled = factorFormControl && factorFormControl.onFilled
    const onEmpty = factorFormControl && factorFormControl.onEmpty

    const checkDirty = React.useCallback(
      (obj: any) => {
        if (isFilled(obj)) {
          if (onFilled) {
            onFilled()
          }
        } else if (onEmpty) {
          onEmpty()
        }
      },
      [onFilled, onEmpty]
    )

    useEnhancedEffect(() => {
      if (isControlled) {
        checkDirty({ value })
      }
    }, [value, checkDirty, isControlled])

    const handleFocus = (
      event: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      // Fix a bug with IE11 where the focus/blur events are triggered
      // while the component is disabled.
      if (fcs.disabled) {
        //@ts-ignore
        event.stopPropagation()
        return
      }

      if (onFocus) {
        onFocus(event as any)
      }
      if (inputPropsProp.onFocus) {
        inputPropsProp.onFocus(event as any)
      }

      if (factorFormControl && factorFormControl.onFocus) {
        factorFormControl.onFocus(event as any)
      } else {
        setFocused(true)
      }
    }

    const handleBlur = (
      event: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (onBlur) {
        onBlur(event as any)
      }
      if (inputPropsProp.onBlur) {
        inputPropsProp.onBlur(event as any)
      }

      if (factorFormControl && factorFormControl.onBlur) {
        factorFormControl.onBlur(event as any)
      } else {
        setFocused(false)
      }
    }

    const handleChange = (
      event: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>,
      ...args: any
    ) => {
      if (!isControlled) {
        //@ts-ignore
        const element = event.target || inputRef.current
        if (element == null) {
          throw new Error(
            'Expected valid input target. ' +
              'Did you use a custom `inputComponent` and forget to forward refs? '
          )
        }

        checkDirty({
          value: element.value,
        })
      }

      if (inputPropsProp.onChange) {
        inputPropsProp.onChange(event, ...args)
      }

      // Perform in the willUpdate
      if (onChange) {
        //@ts-ignore
        onChange(event, ...args)
      }
    }

    // Check the input state on mount, in case it was filled by the user
    // or auto filled by the browser before the hydration (for SSR).
    React.useEffect(() => {
      checkDirty(inputRef.current)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleClick = (
      event: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      if (inputRef.current && event.currentTarget === event.target) {
        inputRef.current.focus()
      }

      if (onClick) {
        onClick(event as any)
      }
    }

    let InputComponent = inputComponent
    let inputProps = inputPropsProp

    if (multiline && InputComponent === 'input') {
      if (rows) {
        if (process.env.NODE_ENV !== 'production') {
          if (minRows || maxRows) {
            console.warn(
              'You can not use the `minRows` or `maxRows` props when the input `rows` prop is set.'
            )
          }
        }
        inputProps = {
          type: undefined,
          minRows: rows,
          maxRows: rows,
          ...inputProps,
        }
      } else {
        inputProps = {
          type: undefined,
          maxRows,
          minRows,
          ...inputProps,
        }
      }

      InputComponent = TextareaAutosize
    }

    const handleAutoFill = (event: any) => {
      // Provide a fake value as Chrome might not let you access it for security reasons.
      checkDirty(
        event.animationName === 'auto-fill-cancel'
          ? inputRef.current
          : { value: 'x' }
      )
    }

    React.useEffect(() => {
      if (factorFormControl) {
        factorFormControl.setAdornedStart(Boolean(startAdornment))
      }
    }, [factorFormControl, startAdornment])

    const ownerState = {
      color: fcs.color || 'primary',
      disabled: fcs.disabled,
      endAdornment,
      error: fcs.error,
      focused: fcs.focused,
      formControl: factorFormControl,
      fullWidth,
      multiline,
      size: fcs.size,
      startAdornment,
      readOnly: readOnly,
      type,
    }

    const Root = slots?.root || InputBaseRoot
    const rootProps = slotProps?.root || {}

    const Input = slots?.input || InputBaseComponent
    const _inputProps = {
      ...inputProps,
      ...slotProps?.input,
      ...(numberInput
        ? { NumberInputProps: NumberInputProps, numberInput }
        : {}),
    }
    const _Input = (
      <Input
        aria-invalid={fcs.error}
        aria-describedby={ariaDescribedby}
        autoComplete={autoComplete}
        /* eslint-disable-next-line */
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        disabled={fcs.disabled}
        id={id}
        onAnimationStart={handleAutoFill}
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        required={fcs.required}
        //  rows={rows}
        value={value}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        type={type}
        spellCheck={false}
        {..._inputProps}
        {...(!isHostComponent(Input) && {
          ownerState,
          Component: InputComponent,
        })}
        ref={handleInputRef}
        className={_inputProps.className}
        // @ts-ignore
        onBlur={handleBlur}
        // @ts-ignore
        onChange={handleChange}
        // @ts-ignore
        onFocus={handleFocus}
        // @ts-ignore
        hasOriginalOnChange={typeof inputPropsProp?.onChange === 'function'}
      />
    )
    return (
      <React.Fragment>
        <Root
          {...rootProps}
          {...(!isHostComponent(Root) && { ownerState, classes })}
          ref={ref}
          onClick={handleClick}
          {...other}
          className={clsx(className, sxFunc(sx))}
        >
          {startAdornment}
          <FormControlContext.Provider value={undefined}>
            {centerAdornment ? (
              <div className={classes?.adornedCenterWrapper}>
                {centerAdornment}
                {_Input}
              </div>
            ) : (
              _Input
            )}
          </FormControlContext.Provider>
          {endAdornment}
          {renderSuffix
            ? renderSuffix({
                ...fcs,
                startAdornment,
              })
            : null}
        </Root>
      </React.Fragment>
    )
  }
)

export interface InputBaseRootProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  classes: InputBaseProps['classes']
  Component: React.ElementType
  ownerState: {
    color: any
    disabled: boolean
    endAdornment: React.ReactNode
    error: boolean
    focused: boolean
    formControl: FormControlState | undefined
    fullWidth: boolean
    multiline: boolean
    size: string
    startAdornment: React.ReactNode
    readOnly: boolean | undefined
  }
}

export interface InputBaseInternalComponentProps
  extends Partial<React.HTMLAttributes<HTMLInputElement>> {
  classes: InputBaseProps['classes']
  Component: React.ElementType
  ownerState: {
    size: string
    multiline: boolean
    type: string
    startAdornment: React.ReactNode
    endAdornment: React.ReactNode
  }
  numberInput?: boolean
  NumberInputProps?: NumberInputProps
  readOnly?: boolean
  disabled?: boolean
  value?: unknown
}

export const InputBaseRoot = React.forwardRef<
  HTMLDivElement,
  InputBaseRootProps
>((props, ref) => {
  const {
    ownerState,
    classes,
    children,
    className,
    Component = 'div',
    ...other
  } = props

  const _classes = clsx(
    [style.root, classes?.root],
    ownerState.error && classes?.error,
    ownerState.focused && classes?.focused,
    [
      style[`size-${ownerState.size}`],
      // @ts-ignore
      classes?.[`size${capitalize(ownerState.size)}`],
    ],
    ownerState.formControl && classes?.formControl,
    ownerState.disabled && clsx(style.disabled, classes?.disabled),
    ownerState.multiline && clsx(style.multiline, classes?.multiline),
    ownerState.fullWidth && clsx(style.fullWidth, classes?.fullWidth),
    ownerState.startAdornment &&
      clsx(style['startAdornment'], classes?.adornedStart),
    ownerState.endAdornment && clsx(style['endAdornment'], classes?.adornedEnd),
    ownerState.readOnly && clsx(style.readOnly, classes?.readOnly),
    className
  )
  return (
    <Component {...other} ref={ref} className={_classes}>
      {children}
    </Component>
  )
})
InputBaseRoot.displayName = 'InputBaseRoot'

export const InputBaseComponent = React.forwardRef<
  HTMLInputElement,
  InputBaseInternalComponentProps
>((props, ref) => {
  const {
    ownerState,
    classes,
    children,
    className,
    numberInput,
    disabled,
    readOnly,
    Component: ComponentProp = 'input',
    NumberInputProps: NumberInputPropsProp,
    ...other
  } = props

  let Component = ComponentProp

  const _classes = clsx(
    [style.input, classes?.input],
    [
      style[`size-${ownerState.size}`],
      // @ts-ignore
      classes?.[`inputSize${capitalize(ownerState.size)}`],
    ],
    disabled && clsx(style.disabled, classes?.inputDisabled),
    ownerState.multiline && clsx(style.multiline, classes?.inputMultiline),
    ownerState.type === 'search' &&
      clsx(style.search, classes?.inputTypeSearch),
    ownerState.startAdornment &&
      clsx(style['startAdornment'], classes?.inputAdornedStart),
    ownerState.endAdornment &&
      clsx(style['endAdornment'], classes?.inputAdornedEnd),
    readOnly && clsx(style.readOnly, classes?.readOnly),
    className
  )

  let NumberInputProps = NumberInputPropsProp

  if (numberInput) {
    NumberInputProps = {
      ...NumberInputProps,
      buttonsContainerClass: clsx(
        NumberInputProps?.buttonsContainerClass,
        style[`size-${ownerState.size}`]
      ),
    }
    Component = InputBaseNumberComponent
  }

  return (
    <Component
      {...other}
      ref={ref}
      className={_classes}
      disabled={disabled}
      {...(numberInput ? { NumberInputProps } : {})}
      readOnly={readOnly}
    >
      {children}
    </Component>
  )
})

InputBaseComponent.displayName = 'InputBaseComponent'

export const InputBaseNumberComponent = React.forwardRef<
  HTMLInputElement,
  InputBaseInternalComponentProps
>((props, ref) => {
  const {
    NumberInputProps,
    Component = 'input',
    children,
    className,
    readOnly: readOnlyProp,
    disabled: disabledProp,
    onKeyDown,
    onChange,
    onBlur,
    onFocus,
    value: valueProp,
    defaultValue: defaultValueProp,
    ...other
  } = props
  const customOnChange = useEventCallback((value: string) => {
    if (typeof onChange === 'function') {
      // @ts-ignore
      onChange({ target: { value: value } })
    }
  })

  const {
    incrementButtonIcon = <InputChevronUp viewBox="0 0 14 8" />,
    decrementButtonIcon = <InputChevronDown viewBox="0 0 14 8" />,
    buttonsContainerClass,
    ...numberInputProps
  } = NumberInputProps!

  const {
    value,
    disabled,
    readOnly,
    getIncrementButtonProps,
    getDecrementButtonProps,
    getInputProps,
    defaultValue,
    htmlProps,
  } = useNumberInput({
    readOnly: readOnlyProp,
    disabled: disabledProp,
    onChange: customOnChange,
    // @ts-ignore
    value: valueProp,
    // @ts-ignore
    defaultValue: defaultValueProp,
    ...numberInputProps,
  })

  return (
    <>
      <Component
        {...getInputProps({
          onFocus: onFocus,
          onBlur: onBlur,
          onKeyDown: onKeyDown,
          ref: ref,
        })}
        readOnly={readOnly}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        {...other}
        {...htmlProps}
        className={className}
      >
        {children}
      </Component>
      <Box
        sx={{ flexDirection: 'column' }}
        className={clsx(style.inputNumberIconContainer, buttonsContainerClass)}
      >
        <IconButton
          size="small"
          sx={{ border: 0, p: 2 }}
          {...getIncrementButtonProps()}
          className={style.inputNumberButton}
        >
          <Typography component="span" variant="span" sx={{ display: 'flex' }}>
            {incrementButtonIcon}
          </Typography>
        </IconButton>
        <IconButton
          size="small"
          sx={{ border: 0, p: 2 }}
          {...getDecrementButtonProps()}
          className={style.inputNumberButton}
        >
          <Typography component="span" variant="span" sx={{ display: 'flex' }}>
            {decrementButtonIcon}
          </Typography>
        </IconButton>
      </Box>
    </>
  )
})

InputBaseNumberComponent.displayName = 'InputBaseNumberComponent'
